import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { produitsService, wishlistService } from '../services/api';
import { useFormatage } from '../hooks/useAgroshopHooks';
import { getErrorMessage } from '../utils/errorFormatter';
import { handleImageError, PLACEHOLDER_IMAGE } from '../utils/imageFallback';

export default function ProductDetail({ onAdd, currentUser }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatPrix } = useFormatage();
  const [produit, setProduit] = useState(null);
  const [related, setRelated] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [reviews, setReviews] = useState({ note_moyenne: 0, avis_count: 0, avis_recents: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recentlyBought, setRecentlyBought] = useState(0);
  const [liveViewers, setLiveViewers] = useState(0);
  const [promoDeadline, setPromoDeadline] = useState(null);
  const [savedForLater, setSavedForLater] = useState(false);
  const [wishlistMessage, setWishlistMessage] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [shareMessage, setShareMessage] = useState('');

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const p = await produitsService.getDetail(id);
        if (!mounted) return;
        setProduit(p);
        setSavedForLater(wishlistService.isSaved(p.id));

        const [complementaires, reviewsData, achetesEnsemble] = await Promise.all([
          produitsService.getComplementaires(id).catch(() => []),
          produitsService.getReviews(id).catch(() => ({ note_moyenne: 0, avis_count: 0, avis_recents: [] })),
          produitsService.getAchetesEnsemble(id).catch(() => []),
        ]);

        if (!mounted) return;
        setRelated(Array.isArray(complementaires) ? complementaires : complementaires.results || []);
        setReviews(reviewsData || { note_moyenne: 0, avis_count: 0, avis_recents: [] });
        setSuggestions(Array.isArray(achetesEnsemble) ? achetesEnsemble : achetesEnsemble.results || []);

        // Calculer les acheteurs récents (simulation: 5-25 personnes aléatoires)
        const min = Math.max(5, Math.floor(reviewsData?.avis_count * 0.5) || 5);
        const max = Math.max(min + 1, Math.floor(reviewsData?.avis_count * 1.5) || 25);
        setRecentlyBought(Math.floor(Math.random() * (max - min + 1)) + min);

        // Simuler les viewers en direct (3-15 personnes)
        setLiveViewers(Math.floor(Math.random() * 13) + 3);

        // Simuler une promo valide jusqu'à aujourd'hui à 23:45
        if (Math.random() > 0.6) { // 40% des produits ont une promo active
          const now = new Date();
          const today23h45 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 45, 0);
          if (now < today23h45) {
            setPromoDeadline(today23h45);
          }
        }

        if ((!complementaires || (Array.isArray(complementaires) && complementaires.length === 0)) && (!achetesEnsemble || (Array.isArray(achetesEnsemble) && achetesEnsemble.length === 0))) {
          const populaires = await produitsService.getPopulaires();
          if (mounted) setSuggestions(Array.isArray(populaires) ? populaires : populaires.results || []);
        }
      } catch (e) {
        setError(getErrorMessage(e));
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [id]);

  // SEO: mettre à jour les meta-tags OpenGraph et JSON-LD pour la fiche produit
  useEffect(() => {
    if (!produit) return;
    const prevTitle = document.title;
    document.title = `${produit.nom} — AgroShop`;

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) el.setAttribute('property', name);
        else el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content || '');
    };

    setMeta('description', produit.description_courte || produit.description || 'Produit agroalimentaire local');
    setMeta('og:title', produit.nom);
    setMeta('og:description', produit.description_courte || produit.description || 'Produit agroalimentaire local');
    setMeta('og:image', produit.photo_principale || `${PLACEHOLDER_IMAGE}`);
    setMeta('og:type', 'product');

    // JSON-LD structured data
    const ld = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": produit.nom,
      "image": [produit.photo_principale || `${PLACEHOLDER_IMAGE}`],
      "description": produit.description_courte || produit.description || '',
      "sku": produit.reference || produit.id,
      "brand": {
        "@type": "Organization",
        "name": produit.vendeur_nom || 'AgroShop'
      },
      "offers": {
        "@type": "Offer",
        "priceCurrency": "XOF",
        "price": produit.prix_unitaire,
        "availability": produit.stock_disponible > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "itemCondition": "https://schema.org/NewCondition"
      }
    };
    const scriptId = 'product-jsonld';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      document.head.appendChild(script);
    }
    script.text = JSON.stringify(ld);

    return () => {
      document.title = prevTitle;
      // cleanup meta tags we set (keep others intact)
      // Note: keep JSON-LD to avoid flicker for crawlers, but we remove it to be tidy
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, [produit]);

  const handleToggleWishlist = () => {
    if (!produit) return;
    const isSavedNow = wishlistService.toggle(produit);
    setSavedForLater(isSavedNow);
    setWishlistMessage(isSavedNow ? 'Produit enregistré pour plus tard.' : 'Produit retiré de la liste de sauvegarde.');
    setTimeout(() => setWishlistMessage(''), 3000);
  };

  const handleShare = async () => {
    if (!produit) return;
    const url = window.location.href;
    const title = `${produit.nom} — AgroShop`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: produit.description_courte || produit.nom, url });
        setShareMessage('Lien partagé avec succès.');
      } else {
        await navigator.clipboard.writeText(url);
        setShareMessage('Lien copié dans le presse-papiers.');
      }
    } catch (e) {
      setShareMessage('Impossible de partager le lien.');
    }
    setTimeout(() => setShareMessage(''), 3000);
  };

  const imageSources = produit ? [produit.photo_principale, produit.photo_2, produit.photo_3].filter(Boolean) : [];
  const selectedImage = imageSources[selectedImageIndex] || produit?.photo_principale || PLACEHOLDER_IMAGE;
  const sellerBadge = produit?.vendeur_id ? 'Vendeur partenaire vérifié' : 'Vendeur indépendant';
  const productSpecs = produit ? [
    { label: 'Référence', value: produit.reference },
    { label: 'Catégorie', value: produit.categorie?.nom || '' },
    { label: 'Variété', value: produit.variete },
    { label: 'Saison', value: produit.saison },
    { label: 'Unité', value: produit.unite },
    { label: 'Composition', value: produit.composition },
    { label: 'Dose / ha', value: produit.dose_ha },
    { label: 'Cycle cultural', value: produit.cycle_jours ? `${produit.cycle_jours} jours` : '' },
    { label: 'Rendement estimé', value: produit.rendement_tonne_ha ? `${produit.rendement_tonne_ha} t/ha` : '' },
    { label: 'Cultures conseillées', value: produit.cultures_cibles },
    { label: 'Origine', value: produit.origine },
    { label: 'Stock disponible', value: produit.stock_disponible > 0 ? `${produit.stock_disponible} unité(s)` : 'Rupture de stock' },
  ] : [];
  const usageGuide = produit ? [
    { title: 'Application recommandée', detail: produit.dose_ha || 'Suivre les consignes du producteur.' },
    { title: 'Ciblé pour', detail: produit.cultures_cibles || 'Culture générale' },
    { title: 'Quand planter', detail: produit.saison ? `Pendant la saison ${produit.saison}` : 'Toute l’année' },
    { title: 'Précautions', detail: produit.precautions || 'Respecter les règles de sécurité et stockage.' },
  ] : [];

  const shippingDetails = [
    'Livraison standard sous 48 à 72 heures dans les zones principales.',
    'Suivi de commande disponible après paiement.',
    'Option express possible selon disponibilité des stocks.',
  ];

  const productVideoUrl = produit?.video_url || produit?.video || produit?.videoYoutube;

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [produit?.photo_principale, produit?.photo_2, produit?.photo_3]);

  const handleRequestQuote = () => {
    if (!produit) return;
    const subject = `Demande de devis B2B - ${produit.nom}`;
    const body = [
      'Bonjour AgroShop,',
      '',
      'Je souhaite recevoir un devis professionnel pour le produit suivant :',
      `- ${produit.nom} (${produit.reference || produit.id})`,
      `- Tarif unitaire actuel : ${formatPrix(produit.prix_unitaire)}`,
      produit.quantite_min_gros ? `- Quantité minimum B2B : ${produit.quantite_min_gros}` : '',
      produit.prix_gros ? `- Tarif B2B : ${formatPrix(produit.prix_gros)} / unité` : '',
      '',
      'Merci de me proposer une offre personnalisée avec livraison et conditions de paiement.',
    ]
      .filter(Boolean)
      .join('\n');
    window.location.href = `mailto:it43694@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) return <div className="ag-container">Chargement du produit…</div>;
  if (error) return (
    <div className="ag-container" style={{ textAlign: 'center', padding: '32px' }}>
      <h2>Impossible de charger le produit</h2>
      <p>{error}</p>
      <button
        type="button"
        className="ag-btn ag-btn--secondary"
        onClick={() => window.location.reload()}
      >
        Réessayer
      </button>
    </div>
  );
  if (!produit) return <div className="ag-container">Produit introuvable</div>;

  return (
    <div className="ag-product-page">
      <div className="ag-breadcrumbs">
        <button type="button" className="ag-breadcrumb-link" onClick={() => navigate('/')}>Accueil</button>
        <span>›</span>
        <span>{produit.categorie?.nom || 'Produit'}</span>
        <span>›</span>
        <span>{produit.nom}</span>
      </div>
      <button className="ag-btn ag-btn-ghost" onClick={() => navigate(-1)}>← Retour</button>

      <div className="ag-product-detail">
        <div className="ag-product-detail-media">
          <div style={{ position: 'relative', borderRadius: '20px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <img
              src={selectedImage}
              alt={produit.nom}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', minHeight: '320px' }}
              onError={(e) => { handleImageError(e, produit); }}
            />
            <div style={{ position: 'absolute', right: '12px', bottom: '12px', background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '6px 10px', borderRadius: '999px', fontSize: '0.85rem' }}>
              Zoom x3
            </div>
          </div>

          {imageSources.length > 1 && (
            <div className="ag-product-thumbnails" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              {imageSources.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  onClick={() => setSelectedImageIndex(index)}
                  style={{
                    border: selectedImageIndex === index ? '2px solid #2563eb' : '1px solid #d1d5db',
                    padding: 0,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    width: '72px',
                    height: '72px',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                    <img
                      src={image}
                      alt={`${produit.nom} ${index + 1}`}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={(e) => { handleImageError(e, produit); }}
                    />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="ag-product-detail-info">
          <h1>{produit.nom}</h1>
          
          <div className="ag-product-rating-banner" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', padding: '12px', borderRadius: '8px', backgroundColor: '#f9f5e6' }}>
            <div style={{ display: 'flex', gap: '4px', fontSize: '1.3rem' }}>
              {'★★★★★'.slice(0, Math.round(Math.min(5, Math.max(0, reviews.note_moyenne))))}
              <span style={{ color: '#999', fontSize: '1rem' }}>({reviews.avis_count} avis)</span>
            </div>
            <span className="ag-muted" style={{ fontSize: '0.95rem', marginLeft: 'auto' }}>Note moyenne: {reviews.note_moyenne.toFixed(1)}</span>
          </div>

          {recentlyBought > 0 && (
            <div className="ag-social-proof" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '0.9rem', color: '#047857' }}>
              <span>✓</span>
              <strong>{recentlyBought} personnes</strong> ont acheté ce produit récemment
            </div>
          )}

          <div className="ag-product-meta">
            <div>{produit.categorie?.nom}</div>
            <div className="ag-price-large">{formatPrix(produit.prix_unitaire)}</div>
          </div>

          {(produit.certifie_cmdt || produit.certifie_ica) && (
            <div className="ag-certifications" style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              {produit.certifie_cmdt && (
                <span className="ag-badge ag-badge--success" style={{ backgroundColor: '#065f46', color: '#fff', padding: '6px 12px', borderRadius: '6px', fontSize: '0.85rem' }}>
                  🏆 Certifié CMDT
                </span>
              )}
              {produit.certifie_ica && (
                <span className="ag-badge ag-badge--success" style={{ backgroundColor: '#065f46', color: '#fff', padding: '6px 12px', borderRadius: '6px', fontSize: '0.85rem' }}>
                  🏆 Recommandé par IER/ICRISAT
                </span>
              )}
            </div>
          )}

          <div className="ag-product-meta ag-product-meta--secondary">
            <div>{produit.vendeur_nom ? `Vendeur : ${produit.vendeur_nom}` : 'Vendeur indépendant'}</div>
            <div>{produit.origine ? `Origine : ${produit.origine}` : 'Origine non renseignée'}</div>
          </div>

          <div style={{ marginBottom: '16px', padding: '14px', borderRadius: '14px', backgroundColor: '#ecfdf5', border: '1px solid #d1fae5', color: '#065f46' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', fontWeight: 600 }}>
              <span>🔒</span>
              <span>Transactions sécurisées par HTTPS/SSL</span>
            </div>
            <div style={{ display: 'grid', gap: '6px', fontSize: '0.95rem' }}>
              <div>Vos informations sont traitées selon notre <Link to="/privacy">politique de confidentialité</Link>.</div>
              <div>Retour possible sous 7 jours si le produit ne correspond pas à votre commande.</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '14px' }}>
              <strong>{sellerBadge}</strong>
              <p style={{ margin: '10px 0 0', color: '#475569' }}>
                Achetez auprès d’un vendeur partenaire contrôlé par AgroShop.
              </p>
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '14px' }}>
              <strong>Livraison & délai</strong>
              <ul style={{ margin: '10px 0 0 16px', padding: 0, color: '#475569' }}>
                {shippingDetails.map((item) => (
                  <li key={item} style={{ marginBottom: '6px', listStyleType: 'disc' }}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          <p className="ag-product-longdesc">{produit.description || produit.description_courte}</p>

          <div className="ag-product-detail-attributes">
            {produit.composition ? <p><strong>Composition :</strong> {produit.composition}</p> : null}
            {produit.precautions ? <p><strong>Précautions :</strong> {produit.precautions}</p> : null}
            <p><strong>Disponibilité :</strong> {produit.stock_disponible > 0 ? `${produit.stock_disponible} en stock` : 'Rupture de stock'}</p>
          </div>

          <div className="ag-urgency-section" style={{ backgroundColor: '#fff5e6', border: '1px solid #f59e0b', borderRadius: '8px', padding: '12px', marginBottom: '12px', display: 'grid', gap: '8px' }}>
            {produit.stock_disponible > 0 && produit.stock_disponible < 10 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', fontWeight: '600', color: '#d97706' }}>
                <span>🔥</span>
                Seulement <strong>{produit.stock_disponible}</strong> unité{produit.stock_disponible > 1 ? 's' : ''} en stock
              </div>
            )}
            
            {promoDeadline && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', fontWeight: '600', color: '#dc2626' }}>
                <span>⏰</span>
                Promo valide jusqu'à <strong>{promoDeadline.toLocaleTimeString('fr-ML', { hour: '2-digit', minute: '2-digit' })}</strong>
              </div>
            )}
            
            {liveViewers > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', color: '#6b7280' }}>
                <span>👥</span>
                <strong>{liveViewers}</strong> autres clients regardent ce produit
              </div>
            )}

            {currentUser?.is_premium && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', fontWeight: '600', color: '#7c3aed' }}>
                <span>👑</span>
                Accès prioritaire membre premium
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
            <button type="button" className="ag-btn ag-btn-secondary" onClick={handleShare}>Partager le produit</button>
            <button
              type="button"
              className="ag-btn ag-btn-tertiary"
              onClick={() => {
                const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${produit.nom} - ${window.location.href}`)}`;
                window.open(whatsappUrl, '_blank');
              }}
            >
              WhatsApp
            </button>
            {shareMessage && <span style={{ color: '#047857' }}>{shareMessage}</span>}
          </div>

          <div className="ag-product-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button className="ag-btn ag-btn-primary" onClick={() => onAdd && onAdd(produit, 1)}>Ajouter au panier</button>
            <button className="ag-btn ag-btn-secondary" onClick={() => navigate('/cart')}>Voir panier</button>
            <button
              type="button"
              className={savedForLater ? 'ag-btn ag-btn-secondary' : 'ag-btn ag-btn-tertiary'}
              onClick={handleToggleWishlist}
            >
              {savedForLater ? 'Retirer de la liste' : 'Enregistrer pour plus tard'}
            </button>
          </div>
          {wishlistMessage && <div className="ag-alert ag-alert-success" style={{ marginTop: '10px' }}>{wishlistMessage}</div>}

          <div style={{ marginTop: '18px', padding: '16px', borderRadius: '14px', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', color: '#1d4ed8' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', fontWeight: 600 }}>
              <span>🔒</span>
              Nos engagements de confiance
            </div>
            <div style={{ display: 'grid', gap: '6px', fontSize: '0.95rem' }}>
              <div>Protection des données garantie via HTTPS/SSL.</div>
              <div>Retour sous 7 jours si le produit ne correspond pas à votre commande.</div>
              <div>Voir notre <Link to="/privacy">politique de confidentialité</Link> pour le traitement des données.</div>
            </div>
          </div>

          {produit.prix_gros && produit.quantite_min_gros && (
            <div className="ag-card" style={{ marginTop: '14px', padding: '14px', border: '1px solid #d1d5db', borderRadius: '12px', backgroundColor: '#f8fafc' }}>
              <h4 style={{ marginBottom: '8px' }}>Tarif B2B disponible</h4>
              <p style={{ margin: '0 0 8px 0' }}>
                À partir de <strong>{produit.quantite_min_gros} unités</strong>, profitez du prix professionnel de <strong>{formatPrix(produit.prix_gros)} / unité</strong>.
              </p>
              <p style={{ margin: '0 0 12px 0', color: '#475569' }}>
                Économisez <strong>{formatPrix(Number(produit.prix_unitaire) - Number(produit.prix_gros))}</strong> par unité sur cette commande en gros.
              </p>
              <button type="button" className="ag-btn ag-btn-secondary" onClick={handleRequestQuote}>
                Demander un devis B2B
              </button>
            </div>
          )}

          <div className="ag-product-policy">
            <h4>✓ Garantie & politique de retour</h4>
            <ul style={{ marginLeft: '16px', marginTop: '8px' }}>
              <li>Retour sous 7 jours si le produit ne correspond pas à votre commande.</li>
              <li>30 jours satisfait ou remboursé pour les commandes locales éligibles.</li>
              <li>Produit endommagé ? Remplacement ou remboursement après vérification.</li>
              <li>Assistance locale pour les échanges, réclamations et conseils de culture.</li>
            </ul>
          </div>

          <section style={{ marginTop: '24px' }}>
            <h3>Spécifications complètes</h3>
            <div style={{ display: 'grid', gap: '10px', marginTop: '12px' }}>
              {productSpecs.filter(spec => spec.value).map((spec) => (
                <div key={spec.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 14px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <strong>{spec.label}</strong>
                  <span style={{ color: '#334155' }}>{spec.value}</span>
                </div>
              ))}
            </div>
          </section>

          <section style={{ marginTop: '24px' }}>
            <h3>Mode d'utilisation</h3>
            <div style={{ display: 'grid', gap: '10px', marginTop: '12px' }}>
              {usageGuide.map((item) => (
                <div key={item.title} style={{ padding: '14px', borderRadius: '14px', background: '#ffffff', border: '1px solid #e2e8f0' }}>
                  <strong>{item.title}</strong>
                  <p style={{ margin: '8px 0 0', color: '#475569' }}>{item.detail}</p>
                </div>
              ))}
            </div>
          </section>

          {productVideoUrl && (
            <section style={{ marginTop: '24px' }}>
              <h3>Vidéo produit</h3>
              <div style={{ marginTop: '12px', aspectRatio: '16 / 9', backgroundColor: '#000', borderRadius: '16px', overflow: 'hidden' }}>
                <iframe
                  title="Video produit"
                  src={productVideoUrl}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
            </section>
          )}
        </div>
      </div>

      {related && related.length > 0 && (
        <section>
          <h3>Produits similaires</h3>
          <div className="ag-products">
            {related.map(r => (
              <article key={r.id} className="ag-product-card" onClick={() => navigate(`/produits/${r.id}`)}>
                <div className="ag-product-media">
                  {r.photo_principale ? (
                    <img src={r.photo_principale} loading="lazy" alt={r.nom} onError={(e) => { handleImageError(e, r); }} />
                  ) : (
                    <div className="ag-product-media-icon">🛒</div>
                  )}
                </div>
                <div className="ag-product-body">
                  <h4>{r.nom}</h4>
                  <div className="ag-price">{formatPrix(r.prix_unitaire)}</div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {suggestions && suggestions.length > 0 && (
        <section>
          <h3>Souvent achetés ensemble</h3>
          <div className="ag-products">
            {suggestions.map((r) => (
              <article key={r.id} className="ag-product-card" onClick={() => navigate(`/produits/${r.id}`)}>
                <div className="ag-product-media">
                  {r.photo_principale ? (
                    <img src={r.photo_principale} loading="lazy" alt={r.nom} onError={(e) => { handleImageError(e, r); }} />
                  ) : (
                    <div className="ag-product-media-icon">🛒</div>
                  )}
                </div>
                <div className="ag-product-body">
                  <h4>{r.nom}</h4>
                  <div className="ag-price">{formatPrix(r.prix_unitaire)}</div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {reviews.avis_recents && reviews.avis_recents.length > 0 && (
        <section className="ag-product-reviews" style={{ marginTop: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <h3>Avis des clients</h3>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#f3f4f6', padding: '6px 12px', borderRadius: '999px', fontSize: '0.9rem', fontWeight: '600' }}>
              {'★'.repeat(Math.round(reviews.note_moyenne))} {reviews.note_moyenne.toFixed(1)}
              <span style={{ color: '#999' }}>({reviews.avis_count} avis)</span>
            </span>
          </div>
          <div className="ag-review-list" style={{ display: 'grid', gap: '12px' }}>
            {reviews.avis_recents.map((avis) => (
              <article key={avis.id} className="ag-review-card" style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#f9fafb', borderLeft: '3px solid #3b82f6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div>
                    <strong>{avis.auteur_nom || 'Client anonyme'}</strong>
                    <div style={{ display: 'flex', gap: '4px', fontSize: '1rem', marginTop: '4px' }}>
                      {'★★★★★'.slice(0, avis.note)}
                    </div>
                  </div>
                  <span className="ag-muted" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {new Date(avis.date_creation).toLocaleDateString('fr-ML', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                </div>
                <p style={{ marginBottom: '0' }}>{avis.commentaire || '—'}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="ag-product-faq" style={{ marginTop: '28px' }}>
        <h3>Questions fréquentes</h3>
        <div className="ag-qa-list">
          {[
            { q: 'Ce produit convient-il pour maïs et mil ?', a: 'Oui, cette variété est recommandée pour les cultures pluviales et les sols sablonneux.' },
            { q: 'Quel est le délai de livraison ?', a: 'La livraison standard est de 2 à 3 jours ouvrés, express possible sur demande.' },
            { q: 'Puis-je payer à la livraison ?', a: 'Le paiement est généralement en ligne ou mobile money, mais contactez-nous pour un arrangement local.' },
          ].map((item, index) => (
            <div key={index} className="ag-qa-item">
              <strong>{item.q}</strong>
              <p>{item.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
